import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  parseWorkspaceCampaignPlacementJob,
  parseWorkspaceCampaignPlacementResult,
  WORKSPACE_CAMPAIGN_PLACEMENT_TERMINAL_STATUSES,
  type WorkspaceCampaignPlacementJob,
  type WorkspaceCampaignPlacementJobCreateBody,
  type WorkspaceCampaignPlacementResult,
} from '@/api/workspaceCampaignPlacementTypes';

const POLL_MS = 2000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollJobUntilDone(
  profileId: string,
  jobId: string,
  onUpdate: (job: WorkspaceCampaignPlacementJob) => void,
  abortRef: { current: boolean }
): Promise<WorkspaceCampaignPlacementJob> {
  for (;;) {
    const res = await adsApi.getWorkspaceCampaignPlacementJob(profileId, jobId);
    const job = parseWorkspaceCampaignPlacementJob(res.data);
    if (!job) throw new Error('Invalid placement job response');
    onUpdate(job);
    if (WORKSPACE_CAMPAIGN_PLACEMENT_TERMINAL_STATUSES.has(job.status)) {
      return job;
    }
    if (abortRef.current) return job;
    await sleep(POLL_MS);
  }
}

export function useWorkspaceCampaignPlacementJob(profileId: string) {
  const queryClient = useQueryClient();
  const [job, setJob] = useState<WorkspaceCampaignPlacementJob | null>(null);
  const [failedResults, setFailedResults] = useState<WorkspaceCampaignPlacementResult[]>([]);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    return () => {
      abortRef.current = true;
    };
  }, [profileId]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['workspace-entity-page', 'campaigns', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
  }, [profileId, queryClient]);

  const loadFailedResults = useCallback(
    async (jobId: string) => {
      const res = await adsApi.listWorkspaceCampaignPlacementJobResults(profileId, jobId, {
        status: 'failed',
        limit: 100,
        offset: 0,
      });
      const raw = (res.data as { results?: unknown[] } | null)?.results ?? [];
      setFailedResults(
        raw
          .map((item) => parseWorkspaceCampaignPlacementResult(item))
          .filter((item): item is WorkspaceCampaignPlacementResult => item != null)
      );
    },
    [profileId]
  );

  const startMutation = useMutation({
    mutationFn: async (body: WorkspaceCampaignPlacementJobCreateBody) => {
      const createRes = await adsApi.createWorkspaceCampaignPlacementJob(profileId, body);
      const created = parseWorkspaceCampaignPlacementJob(createRes.data);
      if (!created) throw new Error('Failed to start placement bidding job');
      setJob(created);
      setFailedResults([]);
      if (WORKSPACE_CAMPAIGN_PLACEMENT_TERMINAL_STATUSES.has(created.status)) {
        if ((created.totals.failed ?? 0) > 0) {
          await loadFailedResults(created.id);
        }
        return created;
      }
      const final = await pollJobUntilDone(profileId, created.id, setJob, abortRef);
      if ((final.totals.failed ?? 0) > 0) {
        await loadFailedResults(final.id);
      }
      return final;
    },
    onSettled: () => {
      invalidate();
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const retryRes = await adsApi.retryWorkspaceCampaignPlacementJob(profileId, jobId);
      const restarted = parseWorkspaceCampaignPlacementJob(retryRes.data);
      if (!restarted) throw new Error('Failed to retry placement bidding job');
      setJob(restarted);
      setFailedResults([]);
      const final = await pollJobUntilDone(profileId, restarted.id, setJob, abortRef);
      if ((final.totals.failed ?? 0) > 0) {
        await loadFailedResults(final.id);
      }
      return final;
    },
    onSettled: () => {
      invalidate();
    },
  });

  const isRunning =
    startMutation.isPending ||
    retryMutation.isPending ||
    (job != null && !WORKSPACE_CAMPAIGN_PLACEMENT_TERMINAL_STATUSES.has(job.status));

  return {
    job,
    failedResults,
    isRunning,
    start: startMutation.mutateAsync,
    retryFailed: retryMutation.mutateAsync,
    reset: () => {
      setJob(null);
      setFailedResults([]);
      startMutation.reset();
      retryMutation.reset();
    },
    error: startMutation.error ?? retryMutation.error,
  };
}
